import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Tooltip } from '@mui/material';
import { Fragment, useMemo } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { translate } from 'react-switch-lang';
import { Alert, Button, Card, CardBody, CardHeader, Col, Container, Row, Table } from 'reactstrap';

import { useTpe } from 'src/hooks/useTpe';
import { formatNumber } from 'src/utils/formatNumber';
import { normalizeGenre } from 'src/utils/utilMethods';
import Breadcrumb from '../../breadcrumb';

const DetailTpePage = (props: any) => {
  const { t } = props;
  const { tpe } = useTpe();
  const history = useHistory();

  const sortedScores = useMemo(() => {
    if (!tpe?.agroecoloscores?.length) return [];

    return [...tpe.agroecoloscores].sort(
      (a, b) => new Date(b.rating_date).getTime() - new Date(a.rating_date).getTime()
    );
  }, [tpe?.agroecoloscores]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';

    const date = new Date(dateString);

    return isNaN(date.getTime()) ? '-' : date.toLocaleDateString('fr-FR');
  };

  if (!tpe) {
    return (
      <Container fluid className="mt-5 text-center">
        <div className="spinner-border text-success" role="status">
          <span className="sr-only">Chargement...</span>
        </div>
      </Container>
    );
  }

  return (
    <Fragment>
      <Breadcrumb parent={t('tpe.list')} url="tpe/list" title={t('tpe.detail')} />

      <Container fluid>
        <Row>
          <Col sm={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  {t('tpe.information')} : {tpe.name}
                </h5>

                <Tooltip title={t('tpe.edit')} arrow>
                  <Link to={`/tpe/edit/${tpe.id}`}>
                    <EditOutlinedIcon
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        fontSize: 26,
                        transition: 'color 0.2s',
                        '&:hover': {
                          color: 'primary.dark',
                        },
                      }}
                    />
                  </Link>
                </Tooltip>
              </CardHeader>

              <CardBody>
                <Row>
                  <Col md={6}>
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.code')}</strong>
                        <span>{tpe.code?.split('/').pop() || '-'}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.name')}</strong>
                        <span>{tpe.name || '-'}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.type')}</strong>
                        <span>{tpe.type || '-'}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.genre')}</strong>
                        <span>{normalizeGenre(tpe.genre) || '-'}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.creation_year')}</strong>
                        <span>{tpe.anneeCreation || '-'}</span>
                      </li>
                    </ul>
                  </Col>

                  <Col md={6}>
                    <ul className="list-group list-group-flush">
                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.total_members')}</strong>
                        <span className="badge badge-success badge-pill">{formatNumber(tpe.nbrMember ?? 0, 0)}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.active_members')}</strong>
                        <span className="badge badge-primary badge-pill">{formatNumber(tpe.nbrActif ?? 0, 0)}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.women_members')}</strong>
                        <span className="badge badge-info badge-pill">{formatNumber(tpe.nbrWomen ?? 0, 0)}</span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('Project')}</strong>

                        <span>
                          {tpe.project ? (
                            <Link
                              to={`${process.env.PUBLIC_URL}/project/detail/${tpe.project.id}`}
                              className="font-weight-bold text-decoration-none"
                            >
                              {tpe.project.name}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </span>
                      </li>

                      <li className="list-group-item d-flex justify-content-between align-items-center">
                        <strong className="text-muted">{t('tpe.localisation')}</strong>
                        <span>{(tpe.commune?.name || '-') + ' / ' + (tpe.village?.name || '-')}</span>
                      </li>
                    </ul>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col sm={12}>
            <Card>
              <CardHeader>
                <h5 className="mb-0">{t('tpe.agroecoloscore_title')}</h5>
              </CardHeader>

              <CardBody>
                {sortedScores.length > 0 ? (
                  <div className="table-responsive">
                    <Table hover className="text-center align-middle">
                      <thead className="thead-light">
                        <tr>
                          <th>{t('tpe.evaluation_date')}</th>
                          <th>{t('tpe.score')}</th>
                        </tr>
                      </thead>

                      <tbody>
                        {sortedScores.map((score: any, index: number) => (
                          <tr key={score.id || index}>
                            <td>{formatDate(score.rating_date)}</td>

                            <td>
                              <span
                                className={`badge badge-pill ${score.rate >= 50 ? 'badge-success' : 'badge-warning'}`}
                              >
                                {score.rate}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <Alert color="info" className="text-center mb-0">
                    {t('tpe.no_scores')}
                  </Alert>
                )}

                <hr className="my-4" />

                <div className="d-flex justify-content-end">
                  <Button color="secondary" onClick={() => history.push('/tpe/list')}>
                    {t('tpe.back')}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default translate(DetailTpePage);
